import React from "react";
import AudioPlayer from "./AudioPlayer";
import VideoPlayer from "./VideoPlayer";
import FileLink from "./FileLink";
import Avatar from "./Avatar";
import { formatDistanceToNow } from "date-fns";
import { Radio, ExternalLink } from "lucide-react";
import linkify from "../../lib/linkify";
import PlusBadge from "./PlusBadge";

function safeDate(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function RoleBadge({ role }) {
  const isAdmin = role !== "user";
  return (
    <span
      data-testid={`post-role-${isAdmin ? "admin" : "artist"}`}
      className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
        isAdmin
          ? "bg-paul-yellow/20 text-paul-yellow"
          : "bg-blue-500/20 text-blue-300"
      }`}
    >
      {isAdmin ? "Admin" : "Artist"}
    </span>
  );
}

export default function PostCard({ post, onDelete }) {
  const isLivePost = post.kind === "live";
  const liveEnded = isLivePost && Boolean(post.liveEndedAt);
  const audio = (post.attachments || []).filter((a) => a.kind === "audio" || a.kind === "voice");
  const video = (post.attachments || []).filter((a) => a.kind === "video");
  const images = (post.attachments || []).filter((a) => a.kind === "image");
  const files = (post.attachments || []).filter((a) => a.kind === "file");

  const author = {
    name: post.authorName || "Paul",
    artistName: post.authorName || "Paul",
    avatarUrl: post.authorAvatarUrl || "",
    lastSeenAt: post.authorLastSeenAt || "",
  };

  // body may be HTML (from TipTap, sanitized server-side) or legacy plain text.
  // Heuristic: if it contains a tag, treat as HTML; otherwise wrap in <p>.
  const rawBody = post.body || "";
  const looksLikeHtml = /<\/?[a-zA-Z][^>]*>/.test(rawBody);

  return (
    <article
      data-testid={`post-card-${post.id}`}
      data-kind={post.kind || "post"}
      className={`bg-[#0f1322] border rounded-2xl p-5 md:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.25)] ${
        isLivePost ? "border-red-500/30" : "border-white/5"
      }`}
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <Avatar user={author} size="sm" testIdPrefix={`post-author-${post.id}`} />
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-white">{post.authorName || "Paul"}</div>
              <RoleBadge role={post.authorRole} />
              <PlusBadge tier={post.authorTier} size="xs" />
            </div>
            <div className="text-xs text-white/40">{safeDate(post.createdAt)}</div>
          </div>
        </div>
        {onDelete && (
          <button
            type="button"
            data-testid={`post-delete-${post.id}`}
            onClick={() => onDelete(post)}
            className="text-xs text-white/40 hover:text-red-400 transition-colors"
          >
            Delete
          </button>
        )}
      </header>

      {post.title && (
        <h3 className="text-lg md:text-xl font-bold text-white mb-2 flex items-center gap-2 flex-wrap">
          {isLivePost && (
            <span
              data-testid={`post-live-tag-${post.id}`}
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded ${
                liveEnded
                  ? "bg-white/10 text-white/60"
                  : "bg-red-500 text-white"
              }`}
            >
              <Radio className="w-3 h-3" /> {liveEnded ? "Live · ended" : "Live"}
            </span>
          )}
          <span>{post.title}</span>
        </h3>
      )}
      {isLivePost && post.liveMeetUrl && !liveEnded && (
        <a
          data-testid={`post-live-join-${post.id}`}
          href={post.liveMeetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold text-sm px-4 py-2 rounded-xl transition-colors mb-3"
        >
          <ExternalLink className="w-4 h-4" />
          Join Google Meet
        </a>
      )}
      {isLivePost && liveEnded && (
        <p
          data-testid={`post-live-ended-${post.id}`}
          className="text-xs text-white/40 mb-3"
        >
          Session ended.
        </p>
      )}
      {rawBody && (
        looksLikeHtml ? (
          <div
            className="post-rendered text-white/85 mb-4"
            // body has already been sanitized server-side via bleach with a tight tag/attr/CSS safelist
            dangerouslySetInnerHTML={{ __html: rawBody }}
          />
        ) : (
          <p
            data-testid={`post-body-${post.id}`}
            className="text-white/80 leading-relaxed whitespace-pre-wrap break-words mb-4"
          >
            {linkify(rawBody, {
              className:
                "underline decoration-paul-yellow/60 hover:decoration-paul-yellow text-paul-yellow break-words",
            })}
          </p>
        )
      )}

      <div className="space-y-3">
        {audio.map((a) => (
          <AudioPlayer key={a.url} src={a.url} name={a.name} />
        ))}
        {video.map((v) => (
          <VideoPlayer key={v.url} src={v.url} name={v.name} />
        ))}
        {images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {images.map((img) => (
              <a key={img.url} href={img.url} target="_blank" rel="noopener noreferrer" className="block">
                <img src={img.url} alt={img.name} className="w-full h-32 object-cover rounded-lg border border-white/5" loading="lazy" />
              </a>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {files.map((f) => (
              <FileLink key={f.url} src={f.url} name={f.name} size={f.size} />
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
